// A full mixer channel strip: input -> EQ -> FX (dist/filter/comp) -> delay/reverb sends -> pan -> fader -> analyser -> dest.
import { EQ_FREQS } from "./theory";
import { makeDistortionCurve } from "./dsp";

export class Channel {
  constructor(engine, id, name, color, dest) {
    this.engine = engine;
    this.id = id;
    this.name = name;
    this.color = color;
    this.gainMul = 1;
    const ctx = engine.ctx;

    this.input = ctx.createGain();

    this.eqBands = EQ_FREQS.map((f, i) => {
      const b = ctx.createBiquadFilter();
      const isShelf = i === 0 || i === EQ_FREQS.length - 1;
      b.type = i === 0 ? "lowshelf" : i === EQ_FREQS.length - 1 ? "highshelf" : "peaking";
      b.frequency.value = f;
      // Shelves need a smooth, non-resonant Q (~0.7) so the bass/treble bands add clean
      // low-end/air instead of a ringing bump+dip. Mids use a wider Q so the graphic EQ
      // is musical and gentle, not surgical/over-sensitive.
      b.Q.value = isShelf ? 0.7 : 0.9;
      b.gain.value = 0;
      return b;
    });
    for (let i = 0; i < this.eqBands.length - 1; i++) this.eqBands[i].connect(this.eqBands[i + 1]);

    this.distortion = ctx.createWaveShaper();
    this.filter = ctx.createBiquadFilter();
    this.filter.type = "lowpass";
    this.filter.frequency.value = 20000;
    this.comp = ctx.createDynamicsCompressor();
    this.comp.threshold.value = 0;
    this.comp.ratio.value = 1;

    this.postFx = ctx.createGain();
    this.dry = ctx.createGain();
    this.dry.gain.value = 1;

    this.delay = ctx.createDelay(1.5);
    this.delay.delayTime.value = 0.3;
    this.delayFb = ctx.createGain();
    this.delayFb.gain.value = 0.35;
    this.delayWet = ctx.createGain();
    this.delayWet.gain.value = 0;

    this.reverb = ctx.createConvolver();
    this.reverb.buffer = engine.impulse;
    this.reverbWet = ctx.createGain();
    this.reverbWet.gain.value = 0;

    this.panner = ctx.createStereoPanner();
    this.fader = ctx.createGain();
    this.fader.gain.value = 0.8;
    this.analyser = ctx.createAnalyser();
    this.analyser.fftSize = 256;

    // wiring
    this.input.connect(this.eqBands[0]);
    this.eqBands[this.eqBands.length - 1]
      .connect(this.distortion)
      .connect(this.filter)
      .connect(this.comp)
      .connect(this.postFx);

    this.postFx.connect(this.dry).connect(this.panner);
    this.postFx.connect(this.delay);
    this.delay.connect(this.delayFb).connect(this.delay);
    this.delay.connect(this.delayWet).connect(this.panner);
    this.postFx.connect(this.reverb).connect(this.reverbWet).connect(this.panner);

    this.panner.connect(this.fader).connect(this.analyser).connect(dest || engine.master);

    this.state = {
      volume: 0.8,
      pan: 0,
      mute: false,
      solo: false,
      eq: new Array(EQ_FREQS.length).fill(0),
      fx: { distortion: 0, filterFreq: 20000, comp: 0, delay: 0, reverb: 0 },
    };
  }

  applySoloMute(anySolo) {
    const s = this.state;
    let g = s.volume * this.gainMul;
    if (s.mute) g = 0;
    else if (anySolo && !s.solo) g = 0;
    this.fader.gain.setTargetAtTime(g, this.engine.ctx.currentTime, 0.01);
  }

  setVolume(v) {
    this.state.volume = v;
    this.engine.refreshSoloMute();
  }
  setPan(v) {
    this.state.pan = v;
    this.panner.pan.setTargetAtTime(v, this.engine.ctx.currentTime, 0.01);
  }
  toggleMute() {
    this.state.mute = !this.state.mute;
    this.engine.refreshSoloMute();
    return this.state.mute;
  }
  toggleSolo() {
    this.state.solo = !this.state.solo;
    this.engine.refreshSoloMute();
    return this.state.solo;
  }
  setEqBand(index, db) {
    this.state.eq[index] = db;
    this.eqBands[index].gain.setTargetAtTime(db, this.engine.ctx.currentTime, 0.01);
  }
  setDistortion(amount) {
    this.state.fx.distortion = amount;
    this.distortion.curve = amount > 0 ? makeDistortionCurve(amount) : null;
  }
  setFilter(freq) {
    this.state.fx.filterFreq = freq;
    this.filter.frequency.setTargetAtTime(freq, this.engine.ctx.currentTime, 0.01);
  }
  setComp(amount) {
    this.state.fx.comp = amount;
    // amount 0..1 => threshold 0..-50, ratio 1..12
    this.comp.threshold.setTargetAtTime(-50 * amount, this.engine.ctx.currentTime, 0.01);
    this.comp.ratio.setTargetAtTime(1 + 11 * amount, this.engine.ctx.currentTime, 0.01);
  }
  setDelay(amount) {
    this.state.fx.delay = amount;
    this.delayWet.gain.setTargetAtTime(amount, this.engine.ctx.currentTime, 0.01);
  }
  setReverb(amount) {
    this.state.fx.reverb = amount;
    this.reverbWet.gain.setTargetAtTime(amount, this.engine.ctx.currentTime, 0.01);
  }
  getLevel() {
    const arr = new Uint8Array(this.analyser.frequencyBinCount);
    this.analyser.getByteTimeDomainData(arr);
    let peak = 0;
    for (let i = 0; i < arr.length; i++) {
      const v = Math.abs(arr[i] - 128) / 128;
      if (v > peak) peak = v;
    }
    return peak;
  }
}
